import React, { useState } from 'react'
import LiveModal from 'live/components/ui/Modal'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import globalNotification from 'live/services/global-notification'
import { RadioGroupCard } from '@intelie/ui'
import { recurrenceOptions, actionOptions } from '../utils'
import api from '../api'
import { Creatable } from 'react-select-virtualized'

const compare = (first: PurgeItem, second: PurgeItem): number => {
    if (first.eventType < second.eventType) {
        return -1
    }
    if (first.eventType > second.eventType) {
        return 1
    }

    return 0
}

type Props = {
    eventTypeOptions: EventTypeOption[]
    closeModal: () => void
    setPurgeRules: React.Dispatch<React.SetStateAction<PurgeRule[]>>
    setPurgeExceptionRules: React.Dispatch<React.SetStateAction<PurgeExceptionRule[]>>
    oldPurgeRule: PurgeRule | null
}

export const PurgeModal = ({
    eventTypeOptions,
    closeModal,
    setPurgeRules,
    setPurgeExceptionRules,
    oldPurgeRule
}: Props): JSX.Element => {
    const [selectedEventType, setSelectedEventType] = useState<EventTypeOption>(
        oldPurgeRule
            ? {
                  label: oldPurgeRule.eventType,
                  value: oldPurgeRule.eventType
              }
            : null
    )
    const [eventTypeError, setEventTypeError] = useState(false)
    const [timeRange, setTimeRange] = useState(oldPurgeRule?.timeRange || '')
    const [timeRangeError, setTimeRangeError] = useState(false)
    const [recurrent, setRecurrent] = useState(oldPurgeRule?.recurrent || false)
    const [action, setAction] = useState(oldPurgeRule?.timeRange ? 'DELETE' : 'KEEP')
    const [saving, setSaving] = useState(false)
    const isEdit = !!oldPurgeRule

    const savePurgeRule = (): void => {
        const purgeRule = { eventType: selectedEventType.value, timeRange, recurrent }
        setSaving(true)
        api.savePurgeRule(purgeRule).then(response => {
            setSaving(false)
            if (response.ok) {
                setEventTypeError(false)
                setTimeRangeError(false)
                setPurgeRules(prevState => [purgeRule, ...prevState].sort(compare))
                globalNotification.success(i18n('New purge rule successfully created'))
                closeModal()
            } else {
                response.json().then(errorResponse => {
                    const errorText = errorResponse.error
                    const eventTypeHasError = errorText.includes('event type')
                    const timeRangeHasError = errorText.includes('time range')
                    if (errorText.includes('already exists')) {
                        globalNotification.error(i18n('Purge rule already exists'))
                    } else {
                        globalNotification.error(i18n('Failed to create the purge rule'))
                        setEventTypeError(eventTypeHasError)
                        setTimeRangeError(timeRangeHasError)
                    }
                })
            }
        })
    }

    const savePurgeExceptionRule = (): void => {
        const purgeExceptionRule = { eventType: selectedEventType.value }
        setSaving(true)
        api.savePurgeExceptionRule(purgeExceptionRule).then(response => {
            setSaving(false)
            if (response.ok) {
                setEventTypeError(false)
                setTimeRangeError(false)
                setPurgeExceptionRules(prevState => [purgeExceptionRule, ...prevState].sort(compare))
                globalNotification.success(i18n('New purge exception successfully created'))
                closeModal()
            } else {
                response.json().then(errorResponse => {
                    const errorText = errorResponse.error
                    const eventTypeHasError = errorText.includes('event type')
                    if (errorText.includes('already exists')) {
                        globalNotification.error(i18n('Purge exception already exists'))
                    } else {
                        globalNotification.error(i18n('Failed to create the purge exception'))
                        setEventTypeError(eventTypeHasError)
                    }
                })
            }
        })
    }

    const editPurgeRule = (): void => {
        const newPurgeRule = { eventType: selectedEventType.value, timeRange, recurrent }
        setSaving(true)
        api.editPurgeRule(oldPurgeRule, newPurgeRule).then(response => {
            setSaving(false)
            if (response.ok) {
                setEventTypeError(false)
                setTimeRangeError(false)
                setPurgeRules(prevState =>
                    [newPurgeRule, ...prevState].filter(prevRule => prevRule !== oldPurgeRule).sort(compare)
                )
                globalNotification.success(i18n('Purge rule successfully edited'))
                closeModal()
            } else {
                response.json().then(errorResponse => {
                    const errorText = errorResponse.error
                    const eventTypeHasError = errorText.includes('event type')
                    const timeRangeHasError = errorText.includes('time range')
                    globalNotification.error(i18n('Failed to edit the purge rule'))
                    setEventTypeError(eventTypeHasError)
                    setTimeRangeError(timeRangeHasError)
                })
            }
        })
    }

    const editPurgeExceptionRule = (): void => {
        const oldPurgeExceptionRule = { eventType: oldPurgeRule.eventType }
        const newPurgeExceptionRule = { eventType: selectedEventType.value }
        setSaving(true)
        api.editPurgeExceptionRule(oldPurgeExceptionRule, newPurgeExceptionRule).then(response => {
            setSaving(false)
            if (response.ok) {
                setEventTypeError(false)
                setTimeRangeError(false)
                setPurgeExceptionRules(prevState =>
                    [...prevState]
                        .filter(prevException => prevException.eventType !== oldPurgeRule.eventType)
                        .concat(newPurgeExceptionRule)
                        .sort(compare)
                )
                globalNotification.success(i18n('Purge exception successfully edited'))
                closeModal()
            } else {
                response.json().then(errorResponse => {
                    const errorText = errorResponse.error
                    const eventTypeHasError = errorText.includes('event type')
                    globalNotification.error(i18n('Failed to edit the purge exception'))
                    setEventTypeError(eventTypeHasError)
                })
            }
        })
    }

    const handleSave = () => {
        const isDeleteAction = action === 'DELETE'

        if (isEdit) {
            isDeleteAction ? editPurgeRule() : editPurgeExceptionRule()
        } else {
            isDeleteAction ? savePurgeRule() : savePurgeExceptionRule()
        }
    }

    return (
        <LiveModal className="modal-sm" show={true} onHide={closeModal}>
            <LiveModal.Header>
                <LiveModal.Title>{isEdit ? i18n('Edit rule') : i18n('New rule')}</LiveModal.Title>
            </LiveModal.Header>
            <LiveModal.Body>
                <div className="form-group">
                    <label>{i18n('Event type')}:</label>
                    <SelectContainer hasError={eventTypeError}>
                        <Creatable
                            options={eventTypeOptions}
                            value={selectedEventType}
                            placeholder={i18n('Insert an event type')}
                            onChange={(option: { label: string; value: string; __isNew__?: boolean }) => {
                                if (option?.__isNew__) {
                                    let newOption = { label: option.value, value: option.value }

                                    return setSelectedEventType(newOption)
                                }

                                return setSelectedEventType(option)
                            }}
                        />
                    </SelectContainer>
                    {eventTypeError && (
                        <ErrorMessage>
                            <i className="far fa-exclamation-triangle" /> {i18n('Invalid event type')}
                        </ErrorMessage>
                    )}
                </div>
                <div className="form-group">
                    <label>{i18n('Time range')}:</label>
                    <Input
                        disabled={action === 'KEEP'}
                        hasError={timeRangeError}
                        autoFocus
                        type="text"
                        value={timeRange}
                        placeholder={i18n('Add a time range')}
                        onChange={e => setTimeRange(e.target.value)}
                    />
                    {timeRangeError && (
                        <ErrorMessage>
                            <i className="far fa-exclamation-triangle" /> {i18n('Invalid time range')}
                        </ErrorMessage>
                    )}
                </div>
                <div className="form-group">
                    <label>{i18n('Action')}:</label>
                    <RadioGroupCard
                        hideLabel={true}
                        label=""
                        name="action"
                        selectedOption={action}
                        options={actionOptions}
                        onChange={value => {
                            setAction(value)
                        }}
                    />
                </div>
                <div className="form-group">
                    <label>{i18n('Recurrence')}:</label>
                    <RadioGroupCard
                        hideLabel={true}
                        readonly={action === 'KEEP'}
                        label=""
                        name="recurrent"
                        selectedOption={recurrent ? 'RECURRENT' : 'ONE_TIME'}
                        options={recurrenceOptions}
                        onChange={value => {
                            if (value === 'RECURRENT') setRecurrent(true)
                            else setRecurrent(false)
                        }}
                    />
                </div>
            </LiveModal.Body>
            <LiveModal.Footer>
                <SubmitOrCancel
                    buttonMessage={isEdit ? i18n('Edit') : i18n('Create')}
                    saving={saving}
                    onSubmit={handleSave}
                    onCancel={closeModal}
                    disabled={!selectedEventType || (timeRange === '' && action === 'DELETE')}
                />
            </LiveModal.Footer>
        </LiveModal>
    )
}

const Input = styled.input<{ hasError?: boolean }>`
    margin-top: 16px;
    box-shadow: none !important;
    border-color: ${({ hasError, theme }) => (hasError ? `${theme['text-danger']} !important` : null)};
`

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme['text-danger']};
    font-size: 13px;
    margin-top: 5px;
`

const SelectContainer = styled.div<{ hasError?: boolean }>`
    div {
        border-color: ${({ hasError, theme }) => (hasError ? `${theme['text-danger']} !important` : null)};
        font-size: 13px;
        margin-bottom: 0px;
    }

    .react-select-virtualized {
        > div {
            background-color: var(--gray-0);
            border-color: var(--gray-500);
            > div > div {
                color: var(--gray-800);
            }
            [class*='placeholder'] {
                color: var(--gray-500);
            }
            [class*='focused'] {
                background-color: var(--blue-200);
            }
            [class*='option-selected'] {
                background-color: var(--blue-500);
                color: var(--static-gray-0);
            }
        }
    }
`
